import {
	getDataUrlByteSize,
	MAX_INSCRIPTION_IMAGE_BYTES,
} from "@/lib/crop-image";

const SVG_DATA_URL_PREFIX = "data:image/svg+xml;base64,";
const BOM_RE = /^\uFEFF/;
const SVG_PREAMBLE_RE =
	/^(?:\s*(?:<\?xml\b[^?]*\?>|<!--[\s\S]*?-->|<!DOCTYPE\b[^>]*>))*/i;
const SVG_ROOT_RE = /^<svg[\s>]/i;
const XML_DECLARATION_RE = /<\?xml\b[^?]*\?>/i;
const DOCTYPE_RE = /<!DOCTYPE\b[^>]*>/i;
const QUOTED_VALUE_RE = /^['"]|['"]$/g;
const JAVASCRIPT_URL_RE = /javascript\s*:/i;
const DATA_URL_RE = /data\s*:/i;
const XMLNS_ATTR_RE = /\bxmlns=/i;
const STYLE_IMPORT_RE = /@import\b[^;]*;?/gi;
const STYLE_EXPRESSION_RE = /expression\s*\(/gi;
const STYLE_JAVASCRIPT_RE = /javascript\s*:/gi;
const STYLE_EXTERNAL_URL_RE = /url\s*\(\s*['"]?\s*(?!#)[^)]*\)/gi;
const TAG_RE =
	/<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\/?([A-Za-z][\w:-]*)([^>]*?)(\/?)>|[^<]+/g;
const ATTR_RE = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
const STYLE_BLOCK_RE = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
const CLASS_RULE_RE = /\.(-?[_a-zA-Z]+[_a-zA-Z0-9-]*)\s*\{([^}]*)\}/g;
const DECL_RE = /([a-zA-Z-]+)\s*:\s*([^;]+)/g;
const OPEN_TAG_RE = /<([A-Za-z][\w:-]*)([^>]*?)(\/?)>/g;
const CLASS_ATTR_RE = /\s*class="([^"]*)"/;
const VIEWBOX_ATTR_RE = /\bviewBox="([^"]+)"/;
const WIDTH_ATTR_RE = /\bwidth=/i;
const HEIGHT_ATTR_RE = /\bheight=/i;
const WHITESPACE_RE = /\s+/;
const VIEWBOX_PARTS_RE = /[\s,]+/;

const ALLOWED_ELEMENTS = new Set([
	"svg",
	"g",
	"path",
	"rect",
	"circle",
	"ellipse",
	"line",
	"polyline",
	"polygon",
	"text",
	"tspan",
	"defs",
	"clippath",
	"lineargradient",
	"radialgradient",
	"stop",
	"title",
	"desc",
	"use",
	"symbol",
	"style",
]);

const ALLOWED_ATTRIBUTES = new Set([
	"viewbox",
	"xmlns",
	"xmlns:xlink",
	"width",
	"height",
	"x",
	"y",
	"x1",
	"y1",
	"x2",
	"y2",
	"cx",
	"cy",
	"r",
	"rx",
	"ry",
	"d",
	"points",
	"fill",
	"fill-opacity",
	"fill-rule",
	"stroke",
	"stroke-width",
	"stroke-opacity",
	"stroke-linecap",
	"stroke-linejoin",
	"stroke-miterlimit",
	"stroke-dasharray",
	"stroke-dashoffset",
	"opacity",
	"transform",
	"class",
	"id",
	"offset",
	"stop-color",
	"stop-opacity",
	"gradientunits",
	"gradienttransform",
	"spreadmethod",
	"fx",
	"fy",
	"clip-path",
	"clippathunits",
	"preserveaspectratio",
	"overflow",
	"display",
	"visibility",
	"font-family",
	"font-size",
	"font-weight",
	"font-style",
	"text-anchor",
	"dominant-baseline",
	"letter-spacing",
	"word-spacing",
	"type",
	"href",
	"xlink:href",
]);

const LOCAL_REF_ATTRIBUTES = new Set(["href", "xlink:href", "clip-path"]);

interface SanitizerState {
	stack: string[];
	output: string;
	skippingDepth: number;
	inStyle: boolean;
	sawRoot: boolean;
}

export function isSvgMarkup(value: string): boolean {
	const trimmed = value.trim().replace(BOM_RE, "");
	if (!trimmed) {
		return false;
	}

	const withoutPreamble = trimmed.replace(SVG_PREAMBLE_RE, "").trim();
	return SVG_ROOT_RE.test(withoutPreamble);
}

export function isSvgDataUrl(value: string): boolean {
	return value.startsWith(SVG_DATA_URL_PREFIX);
}

export function isSvgContentType(contentType: string): boolean {
	return contentType.split(";")[0].trim().toLowerCase() === "image/svg+xml";
}

export function isSvgFile(file: File): boolean {
	return (
		isSvgContentType(file.type) || file.name.toLowerCase().endsWith(".svg")
	);
}

export function decodeSvgDataUrl(value: string): string | null {
	if (!isSvgDataUrl(value)) {
		return null;
	}

	try {
		return base64ToUtf8(value.slice(SVG_DATA_URL_PREFIX.length));
	} catch {
		return null;
	}
}

function utf8ToBase64(value: string): string {
	const bytes = new TextEncoder().encode(value);
	let binary = "";
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary);
}

function base64ToUtf8(value: string): string {
	const binary = atob(value);
	const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
	return new TextDecoder().decode(bytes);
}

function isLocalReference(value: string): boolean {
	const trimmed = value.trim();
	if (trimmed.startsWith("url(") && trimmed.endsWith(")")) {
		const inner = trimmed.slice(4, -1).trim().replace(QUOTED_VALUE_RE, "");
		return inner.startsWith("#");
	}
	return trimmed.startsWith("#");
}

function sanitizeStyleText(css: string): string {
	return css
		.replace(STYLE_IMPORT_RE, "")
		.replace(STYLE_EXPRESSION_RE, "")
		.replace(STYLE_JAVASCRIPT_RE, "")
		.replace(STYLE_EXTERNAL_URL_RE, "");
}

function escapeAttribute(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll('"', "&quot;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;");
}

function stripPreamble(markup: string): string {
	return markup
		.trim()
		.replace(BOM_RE, "")
		.replace(XML_DECLARATION_RE, "")
		.replace(DOCTYPE_RE, "")
		.trim();
}

function allowedAttribute(name: string, value: string): boolean {
	if (name.startsWith("on") || !ALLOWED_ATTRIBUTES.has(name)) {
		return false;
	}
	if (LOCAL_REF_ATTRIBUTES.has(name) && !isLocalReference(value)) {
		return false;
	}
	if (JAVASCRIPT_URL_RE.test(value) || DATA_URL_RE.test(value)) {
		return false;
	}
	return true;
}

function serializeAttributes(rawAttributes: string): string {
	const kept: string[] = [];
	for (const match of rawAttributes.matchAll(ATTR_RE)) {
		const originalName = match[1];
		const value = match[2] ?? match[3] ?? match[4] ?? "";
		if (!allowedAttribute(originalName.toLowerCase(), value)) {
			continue;
		}
		kept.push(`${originalName}="${escapeAttribute(value)}"`);
	}
	return kept.length > 0 ? ` ${kept.join(" ")}` : "";
}

function appendTextToken(state: SanitizerState, token: string): void {
	if (state.skippingDepth > 0) {
		return;
	}
	if (state.inStyle) {
		state.output += sanitizeStyleText(token);
		return;
	}
	if (state.stack.length === 0) {
		if (token.trim()) {
			throw new Error("Invalid SVG");
		}
		return;
	}
	state.output += token;
}

function closeTag(state: SanitizerState, name: string): void {
	if (state.skippingDepth > 0) {
		state.skippingDepth -= 1;
		return;
	}
	if (state.stack.at(-1)?.toLowerCase() !== name) {
		throw new Error("Invalid SVG");
	}
	const openName = state.stack.pop() as string;
	if (name === "style") {
		state.inStyle = false;
	}
	state.output += `</${openName}>`;
}

function openTag(
	state: SanitizerState,
	originalName: string,
	name: string,
	rawAttributes: string,
	isSelfClosing: boolean
): void {
	if (state.skippingDepth > 0 || !ALLOWED_ELEMENTS.has(name)) {
		if (!isSelfClosing) {
			state.skippingDepth += 1;
		}
		return;
	}

	if (state.stack.length === 0) {
		if (name !== "svg" || state.sawRoot) {
			throw new Error("Invalid SVG");
		}
		state.sawRoot = true;
	}

	const attributes = serializeAttributes(rawAttributes);
	if (name === "svg" && !XMLNS_ATTR_RE.test(attributes)) {
		state.output += `<${originalName} xmlns="http://www.w3.org/2000/svg"${attributes}`;
	} else {
		state.output += `<${originalName}${attributes}`;
	}

	if (isSelfClosing) {
		state.output += "/>";
		return;
	}

	state.output += ">";
	state.stack.push(originalName);
	if (name === "style") {
		state.inStyle = true;
	}
}

function applyToken(state: SanitizerState, match: RegExpMatchArray): void {
	const token = match[0];
	const originalName = match[1];
	if (token.startsWith("<!--") || token.startsWith("<![CDATA[")) {
		return;
	}
	if (!originalName) {
		appendTextToken(state, token);
		return;
	}

	const name = originalName.toLowerCase();
	const isClose = token.startsWith("</");
	if (isClose) {
		closeTag(state, name);
		return;
	}

	openTag(state, originalName, name, match[2] ?? "", match[3] === "/");
}

export function sanitizeSvgMarkup(markup: string): string {
	if (!isSvgMarkup(markup)) {
		throw new Error("Invalid SVG");
	}

	const source = stripPreamble(markup);
	TAG_RE.lastIndex = 0;
	const state: SanitizerState = {
		stack: [],
		output: "",
		skippingDepth: 0,
		inStyle: false,
		sawRoot: false,
	};

	for (const match of source.matchAll(TAG_RE)) {
		applyToken(state, match);
	}

	if (state.stack.length > 0 || state.skippingDepth > 0 || !state.sawRoot) {
		throw new Error("Invalid SVG");
	}
	if (!isSvgMarkup(state.output)) {
		throw new Error("Invalid SVG");
	}

	return state.output;
}

function parseClassRules(css: string): Map<string, Map<string, string>> {
	const rules = new Map<string, Map<string, string>>();
	CLASS_RULE_RE.lastIndex = 0;
	for (const match of css.matchAll(CLASS_RULE_RE)) {
		const declarations = new Map<string, string>();
		DECL_RE.lastIndex = 0;
		for (const declaration of match[2].matchAll(DECL_RE)) {
			const property = declaration[1].trim().toLowerCase();
			const value = declaration[2].trim();
			if (ALLOWED_ATTRIBUTES.has(property) && !property.startsWith("on")) {
				declarations.set(property, value);
			}
		}
		if (declarations.size > 0) {
			rules.set(match[1], declarations);
		}
	}
	return rules;
}

function collectClassRules(markup: string): Map<string, Map<string, string>> {
	const rules = new Map<string, Map<string, string>>();
	STYLE_BLOCK_RE.lastIndex = 0;
	for (const block of markup.matchAll(STYLE_BLOCK_RE)) {
		for (const [className, declarations] of parseClassRules(block[1])) {
			const existing = rules.get(className) ?? new Map<string, string>();
			for (const [property, value] of declarations) {
				existing.set(property, value);
			}
			rules.set(className, existing);
		}
	}
	return rules;
}

function hasAttribute(attributes: string, name: string): boolean {
	return new RegExp(`\\b${name}=`, "i").test(attributes);
}

function applyClassPaints(
	attributes: string,
	rules: Map<string, Map<string, string>>
): string {
	const classMatch = CLASS_ATTR_RE.exec(attributes);
	if (!classMatch) {
		return attributes;
	}

	const applied = new Set<string>();
	const extras: string[] = [];
	for (const className of classMatch[1].split(WHITESPACE_RE)) {
		const declarations = rules.get(className);
		if (!declarations) {
			continue;
		}
		for (const [property, value] of declarations) {
			if (applied.has(property) || hasAttribute(attributes, property)) {
				continue;
			}
			applied.add(property);
			extras.push(`${property}="${escapeAttribute(value)}"`);
		}
	}

	let next = attributes.replace(CLASS_ATTR_RE, "");
	if (extras.length > 0) {
		next += ` ${extras.join(" ")}`;
	}
	return next;
}

function applyViewBoxSize(name: string, attributes: string): string {
	if (name.toLowerCase() !== "svg") {
		return attributes;
	}
	if (WIDTH_ATTR_RE.test(attributes) && HEIGHT_ATTR_RE.test(attributes)) {
		return attributes;
	}
	const viewBox = VIEWBOX_ATTR_RE.exec(attributes);
	if (!viewBox) {
		return attributes;
	}
	const parts = viewBox[1].trim().split(VIEWBOX_PARTS_RE);
	if (parts.length !== 4) {
		return attributes;
	}
	let next = attributes;
	if (!WIDTH_ATTR_RE.test(next)) {
		next += ` width="${parts[2]}"`;
	}
	if (!HEIGHT_ATTR_RE.test(next)) {
		next += ` height="${parts[3]}"`;
	}
	return next;
}

export function flattenSvgPresentation(markup: string): string {
	const rules = collectClassRules(markup);
	const withoutStyle = markup.replace(STYLE_BLOCK_RE, "");
	OPEN_TAG_RE.lastIndex = 0;
	return withoutStyle.replace(
		OPEN_TAG_RE,
		(_full, name: string, attributes: string, selfClosing: string) => {
			const next = applyViewBoxSize(name, applyClassPaints(attributes, rules));
			return `<${name}${next}${selfClosing ? "/" : ""}>`;
		}
	);
}

function encodeSvgDataUrl(markup: string): string {
	return `${SVG_DATA_URL_PREFIX}${utf8ToBase64(markup)}`;
}

export function prepareSvgIcon(
	markup: string,
	maxBytes = MAX_INSCRIPTION_IMAGE_BYTES
): string {
	const sanitized = flattenSvgPresentation(sanitizeSvgMarkup(markup));
	const dataUrl = encodeSvgDataUrl(sanitized);
	const size = getDataUrlByteSize(dataUrl);
	if (size > maxBytes) {
		throw new Error(
			`SVG is too large after sanitizing (${size} bytes, max ${maxBytes}). Use a simpler icon.`
		);
	}
	return dataUrl;
}
